// filterButtons.tsx
import React, { useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import "./filterButtons.css"

interface FilterButtonsProps {
    selectedMainView: string;
    onMainViewChange: (view: string) => void;
    selectedView: string;
    onViewChange: (view: string) => void;
    selectedStatus: string;
    onStatusChange: (status: string) => void;
    selectedProduct: string;
    onProductViewChange: (view: string) => void;
    selectedPersonalFilter: string;
    onPersonalFilterChange: (view: string) => void;
}

const FilterButtons: React.FC<FilterButtonsProps> = ({ 
    selectedMainView, 
    onMainViewChange,
    selectedView, 
    onViewChange, 
    selectedStatus, 
    onStatusChange,
    selectedProduct,
    onProductViewChange,
    selectedPersonalFilter,
    onPersonalFilterChange,
}) => {

    // State to track the current destination/market toggle
    const [destinationMode, setDestinationMode] = useState<'destination' | 'market'>('destination');

    const mainViewOptions = [
        { key: 'producer', label: 'Producers' },
        { 
            key: destinationMode, 
            label: destinationMode === 'destination' ? 'Destinations' : 'Markets', 
            icon: 'refresh' 
        },
    ];

    const productOptions = [
        { key: 'crude_oil', label: 'Crude Oil' },
        { key: 'tank_bottoms', label: 'Tank Bottoms' },
        { key: 'ngl', label: 'NGLs' }
    ];

    const viewOptions = [
        { key: 'daily', label: 'Prior Day' },
        { key: 'monthly', label: 'Monthly' },
        { key: 'weekly', label: 'Weekly' }
    ];

    const statusOptions = [
        { key: 'all', label: 'All' },
        { key: 'completed', label: 'Completed' },
        { key: 'rejected', label: 'Rejected' }
    ];

    const producerFilterOptions = [
        { key: 'personal_deals', label: 'Personal Deals' },
        { key: 'all', label: 'All' },
    ];

    const getMainButtonClassName = (viewType: string) => {
        return `filterButtons-timeframeButtons ${selectedMainView === viewType ? 'selected' : ''}`;
    };
    
    const getProductButtonClassName = (viewType: string) => {
        return `filterButtons-timeframeButtons ${selectedProduct === viewType ? 'selected' : ''}`;
    };

    const getPersonalFilterClassName = (viewType: string) => {
        return `filterButtons-timeframeButtons ${selectedPersonalFilter === viewType ? 'selected' : ''}`;
    };

    const getButtonClassName = (viewType: string) => {
        return `filterButtons-timeframeButtons ${selectedView === viewType ? 'selected' : ''}`;
    };

    const getStatusButtonClassName = () => {
        return `filterButtons-statusButton ${selectedStatus === 'all' ? 'all' : selectedStatus === 'completed' ? 'completed' : 'rejected'}`;
    };

    const handleStatusClick = () => {
        const currentIndex = statusOptions.findIndex(option => option.key === selectedStatus);
        const nextIndex = (currentIndex + 1) % statusOptions.length;
        onStatusChange(statusOptions[nextIndex].key);
    };

    const getCurrentStatusLabel = () => {
        const currentOption = statusOptions.find(option => option.key === selectedStatus);
        return currentOption ? currentOption.label : 'All';
    };

    const onFilterChange = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newMode = destinationMode === 'destination' ? 'market' : 'destination';
        setDestinationMode(newMode);
        onMainViewChange(newMode); // Update parent component with new key
    };

    return (
        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
                {mainViewOptions.map(({ key, label, icon }) => (
                    <button
                        key={key}
                        onClick={() => onMainViewChange(key)}
                        className={getMainButtonClassName(key)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <span>{label}</span>
                        {icon && selectedMainView === key && (
                            <RefreshCcw 
                                size={13} 
                                className="filterButtons-buttonIcon"
                                onClick={onFilterChange}
                            />
                        )}
                    </button>
                ))}
                <div style={{ marginTop: '20px' }}>
                    {productOptions.map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => onProductViewChange(key)}
                            className={getProductButtonClassName(key)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <span>{label}</span>
                        </button>
                    ))}
                </div>
                <div style={{ marginTop: '20px' }}>
                    {producerFilterOptions.map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => onPersonalFilterChange(key)}
                            className={getPersonalFilterClassName(key)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <span>{label}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'end'}}>
                {viewOptions.map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => onViewChange(key)}
                        className={getButtonClassName(key)}
                    >
                        {label}
                    </button>
                ))}
                <button
                    onClick={handleStatusClick}
                    className={getStatusButtonClassName()}
                    title={`Current filter: ${getCurrentStatusLabel()}`}
                >
                    {getCurrentStatusLabel()}
                </button>
            </div>
        </div>
    );
};

export default FilterButtons;